import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RecaptureService } from 'src/sb-modules/Frontend-form-field-module/common/services/recapture.service';
import { Subject } from 'rxjs';
import { ReCaptchaConfig } from 'src/sb-modules/Frontend-form-field-module/common/models/re-captcha-config.model';
import { ReCaptchaValidationResponse } from 'src/sb-modules/Frontend-form-field-module/common/models/re-captcha-validation-response.model';

declare const grecaptcha: any;

declare global {
    interface Window {
        grecaptcha: any;
        reCaptchaLoad: () => void;
    }
}

@Component({
    selector: 'sb-recaptcha',
    templateUrl: './recaptcha.component.html',
})
export class ReCaptchaComponent implements OnInit, OnDestroy {
    @ViewChild('reCaptchaWrapperElement') recaptchaElement: ElementRef;

    @Input() key: string;
    @Input() config?: ReCaptchaConfig;
    @Input() lang = '';

    get validationPromise() {
        return this._validationPromise;
    }

    erroneous: boolean;
    errorMessage: string;
    hasResponse: boolean;

    private reCaptchaWidgetId: number;
    private unsubscriber = new Subject<void>();
    private _validationPromise: Promise<ReCaptchaValidationResponse>;

    constructor(
        private translateService: TranslateService,
        private recaptureService: RecaptureService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.render = this.render.bind(this);
        this.responseEventHandler = this.responseEventHandler.bind(this);
        this.expiredEventHandler = this.expiredEventHandler.bind(this);
        this.errorEventHandler = this.errorEventHandler.bind(this);
    }

    ngOnInit() {
        this.registerReCaptchaCallback();
        this.addScript();
    }

    markAsInvalid() {
        this.erroneous = true;
        this.errorMessage = this.translateService.instant('Please confirm that you’re not a robot.');
    }

    reset() {
        this.hasResponse = false;
        grecaptcha.reset(this.reCaptchaWidgetId);
    }

    private addScript() {
        const script = document.createElement('script');
        const lang = this.lang ? '&hl=' + this.lang : '';
        script.src = `https://www.google.com/recaptcha/api.js?onload=reCaptchaLoad&render=explicit${lang}`;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
    }

    private registerReCaptchaCallback() {
        window.reCaptchaLoad = () => {
            const config = {
                ...this.config,
                sitekey: this.key,
                callback: this.responseEventHandler,
                'expired-callback': this.expiredEventHandler,
                'error-callback': this.errorEventHandler,
            };
            this.reCaptchaWidgetId = this.render(this.recaptchaElement.nativeElement, config);
        };
    }

    private render(element: HTMLElement, config): number {
        return grecaptcha.render(element, config);
    }

    private errorEventHandler() {
        this.erroneous = true;

        if (navigator.onLine) {
            this.errorMessage = this.translateService.instant('There was an error. Please try again.');
        } else {
            this.errorMessage = this.translateService.instant(
                'There was an error. Check your internet connection and try again.',
            );
        }

        this.changeDetectorRef.detectChanges();
    }

    private async responseEventHandler(response: string) {
        this.erroneous = false;
        this.errorMessage = '';
        this.hasResponse = true;
        this.changeDetectorRef.detectChanges();

        this._validationPromise = this.verifyAsync(response);
        let isValid;
        try {
            isValid = (await this._validationPromise).success;
        } catch {
            this.erroneous = true;
            if (navigator.onLine) {
                this.errorMessage = this.translateService.instant('Error submitting form. Please try again.');
            } else {
                this.errorMessage = this.translateService.instant(
                    'Error submitting form. Check your internet connection and try again.',
                );
            }
            this.reset();
            this.changeDetectorRef.detectChanges();
            return;
        }

        if (!isValid) {
            this.erroneous = true;
            this.errorMessage = this.translateService.instant(
                'There was an error. Please confirm that you are not a robot.',
            );
            this.reset();
            this.changeDetectorRef.detectChanges();
        }
    }

    private verifyAsync(token) {
        return new Promise<ReCaptchaValidationResponse>((resolve, reject) => {
            this.recaptureService
                .checkReCaptcha({
                    response: token,
                })
                .subscribe(
                    (data) => {
                        resolve(data);
                    },
                    (err) => {
                        reject(err);
                    },
                );
        });
    }

    private expiredEventHandler() {
        this.hasResponse = false;
        delete this._validationPromise;
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
